// Key:
// [HR] Hourly rate
// [HA%] Holiday accrual %
// [THW] Total number of hours worked this holiday year
// [AH] Average number of hours worked per day

// The calculations will be as follows:
// Total monetary holiday accrual:
// [HR] * [HA%] * [THW]
// Total paid time off:
// [HA%] * [THW]
// Total days off:
// [HA%] * ([THW] / [AH])

import { _ } from '../utils.js'
import Validate from '../modules/validate.js'
import errorMessageContainer from '../templates/error.js'
export default class HolidayPayCalculator {
    constructor(el) {
        this.form = el
        this.pristine = new Validate(this.form)
        this.responseContainer = _('response-container', 'element').nodeFrom(
            this.form,
        )
        this.defaultErrorMessage =
            'Something has gone quite wrong, please try to refresh your browser or contact us.'
        // all inputs have a behaviour of calculate for event listener
        this.calculateTriggers = _('calculate').nodesFrom(this.form)

        //  inputs to calculate from
        this.hourlyRate = _('hourly-rate', 'element').nodeFrom(this.form)
        this.holidayAccrual = _('holiday-accrual', 'element').nodeFrom(
            this.form,
        )
        this.averageHours = _('average-hours', 'element').nodeFrom(this.form)

        // totals for calculation text
        this.totalHoursWorked = _('total-hours-worked', 'element').nodeFrom(
            this.form,
        )
        this.totalHolidayAccrual = _(
            'total-holiday-accrual',
            'element',
        ).nodeFrom(this.form)
        this.totalPaidTimeOff = _('total-paid-time-off', 'element').nodeFrom(
            this.form,
        )
        this.totalDaysOff = _('total-days-off', 'element').nodeFrom(this.form)

        // events
        this.calculateTriggers.map((trigger) => {
            trigger.addEventListener('input', this.calculateHolidayPay)
        })
        this.form.addEventListener('submit', this.forceCalculate)
        this.form.addEventListener('reset', this.formReset)
    }

    /**
     * @param  {number} number - to be formatted into comma separated, 2 decimals places sans currency e.g 1234,56.78
     */

    formatNumber = (number) => {
        const formattedNumber = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
        })
            .format(number)
            .replace('£', '')
        return formattedNumber
    }

    forceCalculate = (e) => {
        // on form submit all it does is check whether it's valid and if it is run calculateHolidayPay even though this happens on input anyway
        e.preventDefault()
        const valid = this.pristine.validate()
        if (valid === false) {
            this.handleError(
                "Please check if you've entered everything correctly",
            )
            return false
        }
        this.calculateHolidayPay()
    }

    formReset = () => {
        // reset of values happens because type is submit, the rest reset manually
        this.totalHolidayAccrual.innerHTML = 0
        this.totalPaidTimeOff.innerHTML = 0
        this.totalDaysOff.innerHTML = 0
        this.pristine.reset()
        this.hideLoading()
    }

    calculateHolidayPay = () => {
        const hourlyRateVal = this.hourlyRate.value // [HR]
        const holidayAccrualVal = this.holidayAccrual.value // [HA%]
        const totalHoursWorkedVal = this.totalHoursWorked.value // [THW]
        const averageHoursVal = this.averageHours.value // [AH]

        if (
            hourlyRateVal === '' ||
            holidayAccrualVal === '' ||
            totalHoursWorkedVal === '' ||
            averageHoursVal === '' ||
            parseFloat(hourlyRateVal) > parseFloat(this.hourlyRate.max) ||
            parseFloat(holidayAccrualVal) >
                parseFloat(this.holidayAccrual.max) ||
            parseFloat(totalHoursWorkedVal) >
                parseFloat(this.totalHoursWorked.max) ||
            parseFloat(averageHoursVal) > parseFloat(this.averageHours.max)
        ) {
            return false
        }

        // Total monetary holiday accrual:
        // [HR] * [HA%] * [THW]
        this.totalHolidayAccrual.innerHTML = this.formatNumber(
            hourlyRateVal * (holidayAccrualVal / 100) * totalHoursWorkedVal,
        )

        // Total paid time off:
        // [HA%] * [THW]
        this.totalPaidTimeOff.innerHTML = this.formatNumber(
            (holidayAccrualVal / 100) * totalHoursWorkedVal,
        )

        // Total days off:
        // [HA%] * ([THW] / [AH])
        this.totalDaysOff.innerHTML = this.formatNumber(
            (holidayAccrualVal / 100) * (totalHoursWorkedVal / averageHoursVal),
        )
    }

    /**
     * @param  {string} defaultMessage - error message default set in constructor
     */
    handleError = (defaultMessage = this.defaultErrorMessage) => {
        const errorHTML = `<p>${defaultMessage}</p>`
        this.responseContainer.innerHTML = errorMessageContainer(errorHTML)
    }
}
